import {Theme, createStyles, lighten} from '@material-ui/core';
import {alpha} from '@material-ui/core/styles/colorManipulator';
import {CreateCSSProperties} from '@material-ui/styles/withStyles/withStyles';

import InsideBackground from 'src/Assets/Images/Interactions/inside-background.svg';
import InsideTitleBackground from 'src/Assets/Images/Interactions/inside-title-background.svg';
import OrBlockBackground from 'src/Assets/Images/Interactions/or-arrows.svg';
import OutSideTitleBackground from 'src/Assets/Images/Interactions/outside-title-background.svg';

const getSubTitleCommon = (theme: Theme): CreateCSSProperties => ({
	fontSize: theme.typography.pxToRem(18),
	lineHeight: theme.typography.pxToRem(26),

	[theme.breakpoints.down(theme.breakpoints.values.md)]: {
		fontSize: theme.typography.pxToRem(14),
		lineHeight: theme.typography.pxToRem(22),
	},
});

export const interactionsBlockStyles = (theme: Theme) =>
	createStyles({
		root: {
			[theme.breakpoints.down(theme.breakpoints.values.md)]: {
				backgroundRepeat: 'no-repeat',
				backgroundPosition: '-20% 110%',
				backgroundImage: `url(${InsideBackground})`,
			},
			[theme.breakpoints.down(theme.breakpoints.values.sm)]: {
				paddingTop: theme.spacing(6),
				background: 'none',
			},
		},
		title: {
			fontSize: theme.typography.pxToRem(32),
			lineHeight: theme.typography.pxToRem(42),
			[theme.breakpoints.between(theme.breakpoints.values.sm, theme.breakpoints.values.md)]: {
				paddingLeft: theme.spacing(4),
			},
			[theme.breakpoints.down(theme.breakpoints.values.sm)]: {
				fontSize: theme.typography.pxToRem(18),
				lineHeight: theme.typography.pxToRem(26),
				textAlign: 'center',
			},
		},
		subTitle: getSubTitleCommon(theme),
		insideSubTitle: {
			...getSubTitleCommon(theme),
			backgroundImage: `url(${InsideTitleBackground})`,
			backgroundRepeat: 'no-repeat',
			paddingTop: theme.spacing(5),
			paddingLeft: theme.spacing(7.5),
			paddingBottom: theme.spacing(5),
			backgroundPositionX: theme.spacing(-2),
			backgroundPositionY: theme.spacing(-2),
			[theme.breakpoints.between(theme.breakpoints.values.md, theme.breakpoints.values.lg)]: {
				paddingLeft: theme.spacing(4),
				marginLeft: theme.spacing(2),
			},
			[theme.breakpoints.between(theme.breakpoints.values.sm, theme.breakpoints.values.md)]: {
				paddingLeft: theme.spacing(4),
			},
			[theme.breakpoints.down(theme.breakpoints.values.sm)]: {
				backgroundImage: 'unset',
				backgroundColor: theme.palette.primary.main,
				color: theme.palette.common.white,
				padding: theme.spacing(2.5, 4),
				borderRadius: theme.spacing(1),
				whiteSpace: 'unset',
			},
		},
		outsideSubTitle: {
			...getSubTitleCommon(theme),
			backgroundImage: `url(${OutSideTitleBackground})`,
			backgroundRepeat: 'no-repeat',
			paddingTop: theme.spacing(5),
			paddingLeft: theme.spacing(7),
			paddingBottom: theme.spacing(3),
			marginBottom: theme.spacing(1.5),

			[theme.breakpoints.down(theme.breakpoints.values.sm)]: {
				backgroundImage: 'unset',
				backgroundColor: theme.palette.secondary.main,
				color: theme.palette.text.primary,
				padding: theme.spacing(2.5, 4),
				borderRadius: theme.spacing(1),
				marginRight: theme.spacing(2.5),
				paddingBottom: theme.spacing(5),
				marginLeft: theme.spacing(2.5),
				marginBottom: theme.spacing(-3),
				whiteSpace: 'unset',
			},
		},
		insideDescription: {
			paddingLeft: theme.typography.pxToRem(60),
			lineHeight: theme.typography.pxToRem(26),
			'& + &': {
				paddingTop: theme.spacing(2),
				[theme.breakpoints.down(theme.breakpoints.values.sm)]: {
					paddingTop: 0,
				},
			},
			[theme.breakpoints.between(theme.breakpoints.values.md, theme.breakpoints.values.lg)]: {
				paddingLeft: theme.spacing(6),
				paddingRight: theme.spacing(4),
			},
			[theme.breakpoints.down(theme.breakpoints.values.md)]: {
				paddingLeft: theme.spacing(4),
			},
			[theme.breakpoints.down(theme.breakpoints.values.sm)]: {
				padding: theme.spacing(2, 4),
			},
		},
		description: {
			whiteSpace: 'pre-line',
			paddingTop: theme.spacing(1.5),
			fontSize: theme.typography.pxToRem(16),
			lineHeight: theme.typography.pxToRem(26),
			paddingBottom: theme.spacing(4.5),

			[theme.breakpoints.between(theme.breakpoints.values.sm, theme.breakpoints.values.md)]: {
				paddingLeft: theme.spacing(4),
			},
			[theme.breakpoints.down(theme.breakpoints.values.sm)]: {
				fontSize: theme.typography.pxToRem(12),
				lineHeight: theme.typography.pxToRem(20),
				textAlign: 'center',
			},
		},
		insideBlockWrapper: {
			backgroundRepeat: 'no-repeat',
			backgroundPosition: 'center center',
			backgroundImage: `url(${InsideBackground})`,
			padding: theme.spacing(10, 10, 10, 9),
			transition: 'padding',
			[theme.breakpoints.between(theme.breakpoints.values.md, theme.breakpoints.values.lg)]: {
				padding: theme.spacing(10, 5),
			},
			[theme.breakpoints.down(theme.breakpoints.values.md)]: {
				padding: theme.spacing(7, 5, 5, 1),
				background: 'none',
			},
			[theme.breakpoints.down(theme.breakpoints.values.sm)]: {
				backgroundColor: lighten(theme.palette.primary.light, 0.9),
				padding: theme.spacing(2.5),
			},
		},
		outsideText: {
			padding: theme.spacing(6),
			borderRadius: theme.spacing(2.5),
			boxShadow: ' 0px 14px 32px ' + alpha(theme.palette.primary.main, 0.13),
			[theme.breakpoints.down(theme.breakpoints.values.sm)]: {
				marginRight: theme.spacing(2.5),
				marginLeft: theme.spacing(2.5),
				padding: theme.spacing(4),
			},
		},
		orBlock: {
			color: lighten(theme.palette.grey.A400, 0.2),
			backgroundImage: `url(${OrBlockBackground})`,
			backgroundRepeat: 'no-repeat',
			display: 'flex',
			justifyContent: 'center',
			height: '100%',
			flexDirection: 'column',
			alignItems: 'center',
			backgroundPosition: '50% 52%',
			fontSize: theme.typography.pxToRem(14),

			[theme.breakpoints.down(theme.breakpoints.values.sm)]: {
				minHeight: 200,
			},
		},
		outsideDescription: {
			lineHeight: theme.typography.pxToRem(26),
			'& + &': {
				paddingTop: theme.spacing(3),
			},
		},
	});
